import { IAlarm } from "../../Models/IAlarm";
import { ISensor } from "../../Models/ISensor";
import { ISentinelRich } from "../../Models/ISentinel";
import { ISystemSetting } from "../../Models/ISystemSetting";
import {
  SET_USER,
  GenericAction,
  SET_SENTINELS,
  SET_USERS,
  SET_SYS_SETTINGS,
  SET_AUDIO_ON_SENTINEL_INPUT,
  SET_AUDIO_ON_SENTINEL_OUTPUT,
  UPDATE_SENTINELS_FROM_ALARMS,
  ADD_NEW_ALARM_WEBSOCKET,
  UPDATE_SENTINEL_SENSORS_WEBSOCKET,
} from "./GenericAction";
import { GenericState } from "./GenericState";

export const getLoggedUser = () => {
  let u = localStorage.getItem("loggedUser");
  if (u) {
    return JSON.parse(u);
  } else {
    return undefined;
  }
};

export const getSystemSetting = (key: string) => {
  let settings: any = localStorage.getItem("system-settings");

  if (settings) {
    let settingsParsed: ISystemSetting[] = JSON.parse(settings);

    for (let i = 0; i < settingsParsed.length; i++) {
      if (settingsParsed[i].key === key && settingsParsed[i].enabled) {
        return settingsParsed[i].value;
      }
    }
  }
  return null;
};

const DEFAULT_STATE: GenericState = {
  user: getLoggedUser(),
  users: [],
  sentinels: [],
  systemSettings: [],
  audioOnSentinelInput: -1,
  audioOnSentinelOutput: -1,
};

export const genericReducer = (
  state: GenericState = DEFAULT_STATE,
  action: GenericAction
): GenericState => {
  switch (action.type) {
    case SET_SYS_SETTINGS:
      if (action.payload) {
        localStorage.setItem("system-settings", JSON.stringify(action.payload));
      } else {
        localStorage.removeItem("system-settings");
      }
      return {
        ...state,
        systemSettings: action.payload,
      };
    case SET_AUDIO_ON_SENTINEL_INPUT:
      return {
        ...state,
        audioOnSentinelInput: action.payload,
      };
    case SET_AUDIO_ON_SENTINEL_OUTPUT:
      return {
        ...state,
        audioOnSentinelOutput: action.payload,
      };
    case SET_USERS:
      return {
        ...state,
        users: action.payload,
      };
    case SET_USER:
      if (action.payload) {
        localStorage.setItem("loggedUser", JSON.stringify(action.payload));
      } else {
        localStorage.removeItem("loggedUser");
      }
      return {
        ...state,
        user: action.payload,
      };
    case SET_SENTINELS:
      return {
        ...state,
        sentinels: action.payload,
      };
    case UPDATE_SENTINELS_FROM_ALARMS:
      // OTTIMIZZATO: Aggiorna SOLO i sensori che hanno effettivamente cambiato valore
      const alarms: IAlarm[] = action.payload;
      let hasChanges = false;
      
      const updatedSentinels: ISentinelRich[] = state.sentinels.map((sentinel: ISentinelRich) => {
        // Trova gli allarmi per questo sentinel
        const sentinelAlarms = alarms.filter((alarm: IAlarm) => 
          alarm.sensor.sentinelId === sentinel.id
        );
        
        if (sentinelAlarms.length === 0) {
          // Nessun allarme per questo sentinel - ritorna l'oggetto originale (no re-render)
          return sentinel;
        }
        
        // Verifica se ci sono cambiamenti effettivi nei sensori
        let sensorsChanged = false;
        const updatedSensors = sentinel.sensors.map((sensor: ISensor) => {
          const alarmForSensor = sentinelAlarms.find((alarm: IAlarm) => 
            alarm.sensor.id === sensor.id
          );
          
          if (alarmForSensor) {
            // Confronta i valori per vedere se sono cambiati
            const valueChanged = sensor.lastValue !== alarmForSensor.sensor.lastValue;
            const timeChanged = sensor.updatedAt !== alarmForSensor.sensor.updatedAt;
            
            if (valueChanged || timeChanged) {
              // SOLO se i dati sono cambiati, crea un nuovo oggetto
              sensorsChanged = true;
              return {
                ...sensor,
                lastValue: alarmForSensor.sensor.lastValue,
                updatedAt: alarmForSensor.sensor.updatedAt,
              };
            }
          }
          // Nessun cambiamento - ritorna l'oggetto originale
          return sensor;
        });
        
        if (sensorsChanged) {
          // SOLO se almeno un sensore è cambiato, crea un nuovo sentinel
          hasChanges = true;
          return {
            ...sentinel,
            sensors: updatedSensors,
          };
        }
        
        // Nessun cambiamento - ritorna l'oggetto originale (no re-render)
        return sentinel;
      });
      
      // SOLO se ci sono stati cambiamenti, ritorna un nuovo state
      if (hasChanges) {
        return {
          ...state,
          sentinels: updatedSentinels,
        };
      }
      
      // Nessun cambiamento - ritorna lo state originale (no re-render globale)
      return state;
    
    case ADD_NEW_ALARM_WEBSOCKET:
      // Gestisce nuovo allarme ricevuto via WebSocket
      // NOTA: AlarmGalleryHandler gestisce già la visualizzazione degli allarmi tramite polling HTTP
      // Questo case serve per eventuali future integrazioni (es. aggiornare contatore allarmi in header)
      console.log('🚨 [Redux] Nuovo allarme WebSocket ricevuto:', action.payload);
      
      // Per ora ritorna lo state invariato - AlarmGalleryHandler gestisce già gli allarmi
      // In futuro potremmo aggiungere un array di allarmi nello state Redux
      return state;
    
    case UPDATE_SENTINEL_SENSORS_WEBSOCKET:
      // Gestisce aggiornamento sensori ricevuto via WebSocket
      const sensorData = action.payload;
      console.log('📡 [Redux] Aggiornamento sensori WebSocket ricevuto:', sensorData);
      
      // ⚠️ FIX: Validazione payload per evitare crash
      if (!sensorData || !sensorData.sentinelId || !sensorData.sensors) {
        console.warn('⚠️ Payload WebSocket invalido:', sensorData);
        return state;
      }
      
      // Aggiorna i sensori del sentinel specifico con i dati real-time
      const updatedSentinelsWS: ISentinelRich[] = state.sentinels.map((sentinel: ISentinelRich) => {
        if (sentinel.id === sensorData.sentinelId) {
          // ⚠️ FIX: Null-safety per sentinel.sensors
          if (!sentinel.sensors || !Array.isArray(sentinel.sensors)) {
            console.warn('⚠️ Sentinel senza sensori validi:', sentinel.id);
            return sentinel;
          }
          
          // Aggiorna i sensori con i nuovi valori
          const updatedSensorsWS = sentinel.sensors.map((sensor: ISensor) => {
            const sensorUpdate = sensorData.sensors?.find((s: any) => s.type === sensor.type);
            
            if (sensorUpdate) {
              return {
                ...sensor,
                lastValue: sensorUpdate.value,
                previousValue: sensorUpdate.previousValue,
                updatedAt: sensorUpdate.timestamp,
              };
            }
            return sensor;
          });
          
          return {
            ...sentinel,
            sensors: updatedSensorsWS,
          };
        }
        return sentinel;
      });
      
      return {
        ...state,
        sentinels: updatedSentinelsWS,
      };
    
    default:
      return state;
  }
};
